import React, { createContext, useContext, useEffect, useState } from 'react';
import API_URL from '../config/api';
import {
  buildFreshUrl,
  CONTENT_SYNC_KEYS,
  NO_CACHE_JSON_HEADERS,
  subscribeToContentUpdates,
} from '../utils/contentSync';
import {
  getSessionCache,
  removeSessionCache,
  setSessionCache,
} from '../utils/sessionCache';

const SiteContentContext = createContext({
  siteContent: null,
  isSiteContentLoading: true,
});

const SITE_CONTENT_CACHE_KEY = 'site-content-v20260509a';
const SITE_CONTENT_CACHE_TTL_MS = 10 * 60 * 1000;

export function SiteContentProvider({ children }) {
  const [siteContent, setSiteContent] = useState(null);
  const [isSiteContentLoading, setIsSiteContentLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    const cachedSiteContent = getSessionCache(
      SITE_CONTENT_CACHE_KEY,
      SITE_CONTENT_CACHE_TTL_MS
    );

    if (cachedSiteContent) {
      setSiteContent(cachedSiteContent);
      setIsSiteContentLoading(false);
    }

    const fetchSiteContent = async () => {
      try {
        const response = await fetch(buildFreshUrl(API_URL.SITE_CONTENT), {
          signal: controller.signal,
          cache: 'no-store',
          headers: {
            ...NO_CACHE_JSON_HEADERS,
          },
        });

        if (response.status === 404) {
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch site content: ${response.status}`);
        }

        const data = await response.json();

        if (!cancelled && data.success && data.data) {
          setSiteContent(data.data);
          setSessionCache(SITE_CONTENT_CACHE_KEY, data.data);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          const message = String(error?.message || '');

          if (message.includes('404')) {
            return;
          }

          console.error('Failed to fetch site content:', error);
        }
      } finally {
        if (!cancelled) {
          setIsSiteContentLoading(false);
        }
      }
    };

    fetchSiteContent();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [refreshToken]);

  useEffect(
    () =>
      subscribeToContentUpdates({
        keys: [
          CONTENT_SYNC_KEYS.SITE_CONTENT,
          CONTENT_SYNC_KEYS.PRODUCT_CATEGORY_SLIDER,
        ],
        onUpdate: () => {
          removeSessionCache(SITE_CONTENT_CACHE_KEY);
          setRefreshToken((current) => current + 1);
        },
        refreshOnFocus: false,
        refreshOnVisibilityChange: false,
      }),
    []
  );

  return (
    <SiteContentContext.Provider value={{ siteContent, isSiteContentLoading }}>
      {children}
    </SiteContentContext.Provider>
  );
}

export function useSiteContent() {
  return useContext(SiteContentContext);
}
